import React, { useMemo, useState } from 'react';
import { X, Clock, Users, MapPin, ImageIcon, FileText, StickyNote, UserCheck, History } from 'lucide-react';
import {
  CommonArea,
  CommonAreaBooking,
  BookingStatus,
} from '../../services/commonAreaReservationService';
import { cn } from '../../utils/cn';
import { areaPhotos } from './areaPhotos';
import { bookingDayLabel, bookingFullDayLabel, bookingHoursLabel } from './bookingLabels';
import BookingActions from './BookingActions';
import BookingIdChip from './BookingIdChip';
import { BookingCheckinPanel } from './BookingCheckinPanel';
import { bookingStatusChip } from './bookingStatusStyles';

/**
 * One reservation, opened.
 *
 * The operational list could approve, cancel and check a reservation in, but never SHOW it — no
 * photo of the space, no finalidade, no observações, no trail of who created or approved it, and
 * no way to see what else that resident has booked. An operator had to read a six-column row and
 * guess, then call the resident back when they guessed wrong.
 *
 * Laid out like the person modal and Nova Reserva on purpose: the space on the left, because that
 * is how an operator recognises which área a resident is asking about over the counter, and what
 * is being decided on the right.
 */

export interface ReservaDetailModalProps {
  booking: CommonAreaBooking;
  areas: CommonArea[];
  /** Everything the screen has loaded — the resident's other reservations are drawn from it. */
  allBookings?: CommonAreaBooking[];
  lightTheme?: boolean;
  processing?: boolean;
  onApprove?: (b: CommonAreaBooking) => void;
  onRevoke?: (b: CommonAreaBooking) => void;
  onCancel?: (b: CommonAreaBooking) => void;
  onViewPerson?: (personId: string) => void;
  onUpdated?: () => void;
  onClose: () => void;
}

const personName = (b: CommonAreaBooking): string => {
  const p: any = (b as any).person;
  const full = [p?.first_name, p?.last_name].filter(Boolean).join(' ').trim();
  return full || 'Morador';
};

const unitLabel = (b: CommonAreaBooking): string | null =>
  (b as any).unit_label || (b as any).person?.unit_label || null;

/** "19/10/2026 12:00" — for the audit trail, where the exact moment matters. */
const stamp = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const ReservaDetailModal: React.FC<ReservaDetailModalProps> = ({
  booking,
  areas,
  allBookings = [],
  lightTheme = true,
  processing = false,
  onApprove,
  onRevoke,
  onCancel,
  onViewPerson,
  onUpdated,
  onClose,
}) => {
  // Opening one of the resident's other reservations swaps what this modal shows rather than
  // stacking a second modal on top of the first.
  const [shownId, setShownId] = useState(booking.id);
  const shown = useMemo(
    // The `booking` prop wins for its own id: the parent re-renders it with a fresh status right
    // after an approve, while its loaded list is still the one from before the request.
    () => (shownId === booking.id ? booking : allBookings.find(b => b.id === shownId) ?? booking),
    [allBookings, shownId, booking],
  );

  const area = useMemo(
    () => areas.find(a => a.id === shown.common_area_id) ?? null,
    [areas, shown.common_area_id],
  );
  const photos = useMemo(() => areaPhotos(area), [area]);
  const [photoIndex, setPhotoIndex] = useState(0);

  const others = useMemo(
    () => allBookings
      .filter(b => b.id !== shown.id && !!shown.person_id && b.person_id === shown.person_id)
      .sort((a, b) => b.start_time.localeCompare(a.start_time)),
    [allBookings, shown],
  );

  const status = shown.status as BookingStatus;
  const canApprove = status === 'Pending';
  const canRevoke = status === 'Confirmed' || status === 'Approved';
  const canCancel = status === 'Pending' || status === 'Confirmed' || status === 'Approved';

  const text = lightTheme ? 'text-gray-900' : 'text-white';
  const subtext = lightTheme ? 'text-gray-500' : 'text-gray-400';
  const divider = lightTheme ? 'border-gray-200' : 'border-gray-700';
  const panel = lightTheme ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-700';
  const sidePanel = lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-950 border-gray-800';
  const block = lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-800/50 border-gray-700';
  const statusChip = bookingStatusChip(status, lightTheme);

  const Field = ({ icon: Icon, label, children }: { icon: React.ElementType; label: string; children: React.ReactNode }) => (
    <div>
      <p className={cn('flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide mb-0.5', subtext)}>
        <Icon className="h-3 w-3" />
        {label}
      </p>
      <div className={cn('text-sm', text)}>{children}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className={cn('w-full max-w-4xl rounded-2xl shadow-2xl border flex flex-col max-h-[92vh]', panel)}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn('flex items-center justify-between px-6 py-4 border-b flex-shrink-0', divider)}>
          <div className="flex items-center gap-3 min-w-0">
            <h3 className={cn('text-base font-bold', text)}>Reserva</h3>
            <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold border flex-shrink-0', statusChip.chip)}>
              <span className={cn('h-1.5 w-1.5 rounded-full flex-shrink-0', statusChip.dot)} />
              {statusChip.label}
            </span>
            <BookingIdChip bookingId={shown.id} lightTheme={lightTheme} />
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-gray-800 text-gray-400')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col md:flex-row">
            {/* ── The space, on the left ─────────────────────────────────────── */}
            <aside className={cn('md:w-72 flex-shrink-0 border-b md:border-b-0 md:border-r p-4 space-y-3', sidePanel, divider)}>
              <div className={cn('aspect-[4/3] w-full rounded-xl overflow-hidden flex items-center justify-center border', divider,
                lightTheme ? 'bg-white' : 'bg-gray-900')}>
                {photos.length > 0 ? (
                  <img src={photos[Math.min(photoIndex, photos.length - 1)]} alt={area?.name || 'Área comum'} className="w-full h-full object-cover" />
                ) : (
                  <div className={cn('flex flex-col items-center gap-1', subtext)}>
                    <ImageIcon className="h-7 w-7" />
                    <span className="text-[11px]">Sem foto cadastrada</span>
                  </div>
                )}
              </div>

              {photos.length > 1 && (
                <div className="grid grid-cols-4 gap-1">
                  {photos.map((url, i) => (
                    <button
                      key={url}
                      type="button"
                      aria-label={`Foto ${i + 1}`}
                      onClick={() => setPhotoIndex(i)}
                      className={cn('aspect-[4/3] rounded border overflow-hidden', i === photoIndex ? 'border-blue-500 ring-1 ring-blue-400' : divider)}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {area && (
                <div className="space-y-1.5">
                  <p className={cn('text-sm font-bold leading-tight', text)}>{area.name}</p>
                  {area.description && <p className={cn('text-xs', subtext)}>{area.description}</p>}
                  <div className={cn('flex items-center gap-1.5 text-xs', subtext)}>
                    <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>{bookingHoursLabel(shown, area)}</span>
                  </div>
                  {area.capacity > 0 && (
                    <div className={cn('flex items-center gap-1.5 text-xs', subtext)}>
                      <Users className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>Capacidade: {area.capacity}</span>
                    </div>
                  )}
                  {area.requires_approval && (
                    <p className={cn('flex items-center gap-1.5 text-xs font-medium', lightTheme ? 'text-gray-700' : 'text-gray-200')}>
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                      Esta área requer aprovação do síndico
                    </p>
                  )}
                  {area.rules && <p className={cn('text-xs whitespace-pre-line', subtext)}>{area.rules}</p>}
                </div>
              )}
            </aside>

            {/* ── The reservation, on the right ──────────────────────────────── */}
            <div className="flex-1 p-4 space-y-4 min-w-0">
              {/* Who */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {onViewPerson && shown.person_id ? (
                    <button
                      type="button"
                      onClick={() => onViewPerson(shown.person_id!)}
                      className={cn('text-base font-bold text-left hover:underline', lightTheme ? 'text-gray-900 hover:text-blue-600' : 'text-white hover:text-blue-400')}
                    >
                      {personName(shown)}
                    </button>
                  ) : (
                    <p className={cn('text-base font-bold', text)}>{personName(shown)}</p>
                  )}
                  {unitLabel(shown) && (
                    <p className={cn('text-xs font-medium', lightTheme ? 'text-blue-600' : 'text-blue-400')}>
                      {unitLabel(shown)}
                    </p>
                  )}
                </div>
                <BookingActions
                  lightTheme={lightTheme}
                  processing={processing}
                  onApprove={canApprove && onApprove ? () => onApprove(shown) : undefined}
                  onRevoke={canRevoke && onRevoke ? () => onRevoke(shown) : undefined}
                  onCancel={canCancel && onCancel ? () => onCancel(shown) : undefined}
                  onToggleDetails={() => { /* the panel is already open below */ }}
                  detailsOpen
                  checkedIn={!!shown.checked_in_at}
                />
              </div>

              {/* When & where */}
              <div className={cn('grid grid-cols-2 gap-3 rounded-lg border p-3', block)}>
                <Field icon={Clock} label="Quando">
                  <span className="font-semibold">{bookingFullDayLabel(shown)}</span>
                  <br />
                  <span className={subtext}>{bookingHoursLabel(shown, area)}</span>
                </Field>
                <Field icon={MapPin} label="Área">
                  {area?.name ?? '—'}
                  {(shown as any).resource?.name && (
                    <>
                      <br />
                      <span className={subtext}>{(shown as any).resource.name}</span>
                    </>
                  )}
                </Field>
              </div>

              {shown.purpose && (
                <Field icon={FileText} label="Finalidade">{shown.purpose}</Field>
              )}
              {shown.notes && (
                <Field icon={StickyNote} label="Observações">
                  <span className="whitespace-pre-line">{shown.notes}</span>
                </Field>
              )}

              {/* Trail — who did what to this reservation, which the row never showed. */}
              <div className={cn('rounded-lg border p-3 space-y-1 text-xs', block, subtext)}>
                <p className={cn('flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide', subtext)}>
                  <UserCheck className="h-3 w-3" />
                  Histórico
                </p>
                <p>Criada {stamp(shown.created_at)}{(shown as any).performed_by_name ? ` por ${(shown as any).performed_by_name}` : ''}</p>
                {shown.approved_at && (
                  <p>Aprovada {stamp(shown.approved_at)}{(shown as any).approved_by_name ? ` por ${(shown as any).approved_by_name}` : ''}</p>
                )}
                {(shown as any).revoked_at && (
                  <p>
                    Revogada {stamp((shown as any).revoked_at)}
                    {(shown as any).revoked_by_name ? ` por ${(shown as any).revoked_by_name}` : ''}
                    {(shown as any).revoked_reason ? ` — ${(shown as any).revoked_reason}` : ''}
                  </p>
                )}
                {!(shown as any).revoked_at && (shown as any).revoked_by_name && (
                  <p>
                    Revogada por {(shown as any).revoked_by_name}
                    {(shown as any).revoked_reason ? ` — ${(shown as any).revoked_reason}` : ''}
                  </p>
                )}
                {shown.checked_in_at && (
                  <p>Check-in {stamp(shown.checked_in_at)}{(shown as any).checked_in_by_name ? ` por ${(shown as any).checked_in_by_name}` : ''}</p>
                )}
                {shown.late_cancellation_fee_applied && (
                  <p className={cn('font-semibold', lightTheme ? 'text-gray-700' : 'text-gray-200')}>
                    Multa de cancelamento tardio aplicada
                    {shown.late_cancellation_fee_amount ? `: R$ ${shown.late_cancellation_fee_amount.toFixed(2)}` : ''}
                  </p>
                )}
              </div>

              {/* Check-in, guests and checklist — the panel the row used to hide behind a glyph. */}
              {area && (
                <BookingCheckinPanel
                  area={area}
                  booking={shown}
                  variant="staff"
                  darkMode={!lightTheme}
                  onUpdated={() => onUpdated?.()}
                />
              )}

              {/* The resident's other reservations */}
              <div data-testid="outras-reservas">
                <p className={cn('flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide mb-1', subtext)}>
                  <History className="h-3 w-3" />
                  Outras reservas deste morador
                </p>
                {others.length === 0 ? (
                  <p className={cn('text-xs', subtext)}>Nenhuma outra reserva no período carregado.</p>
                ) : (
                  <ul className="space-y-1">
                    {others.map(o => {
                      const oArea = areas.find(a => a.id === o.common_area_id) ?? null;
                      const otherChip = bookingStatusChip(o.status as BookingStatus, lightTheme);
                      return (
                        <li key={o.id}>
                          <button
                            type="button"
                            onClick={() => { setShownId(o.id); setPhotoIndex(0); }}
                            className={cn('w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border text-xs transition-colors',
                              lightTheme ? 'border-gray-200 hover:bg-gray-50' : 'border-gray-700 hover:bg-gray-800')}
                          >
                            <span className={cn('font-medium', text)}>
                              {bookingDayLabel(o)} · {oArea?.name ?? 'Área'}
                            </span>
                            <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold flex-shrink-0', otherChip.chip)}>
                              <span className={cn('h-1 w-1 rounded-full flex-shrink-0', otherChip.dot)} />
                              {otherChip.label}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservaDetailModal;
